import {Component, OnInit, AfterViewInit, HostListener} from '@angular/core';
import {LbChartHeaderComponent} from '../lb-chart-header/lb-chart-header.component';
import {ChartsCommonFunctions} from 'src/app/shared/toolbox/charts/chartsCommonFunctions';
import * as Plotly from 'plotly.js-dist-min'
import {ViewChild} from '@angular/core';
import {ElementRef} from '@angular/core';
import {Input} from '@angular/core';

@Component({
  standalone: true,
  selector: 'app-lb-bar-chart',
  templateUrl: './lb-bar-chart.component.html',
  styleUrls: ['./lb-bar-chart.component.scss'],
  imports: [
    LbChartHeaderComponent
  ]
})
export class LbBarChartComponent implements OnInit, AfterViewInit {
  constructor(private common: ChartsCommonFunctions) {
  }

  @ViewChild('plotContainer', {static: true}) plotContainer!: ElementRef;

  @Input() data: any | null = {
    y: ['Sales ', 'Search Demand ', 'Market Share ', 'NPS Score ', 'Traffic ', 'Customer Satisfaction ', 'Product Quality ', 'Brand Loyalty '], // x-axis data for first trace
    x: [1, 3, 2, 4, 5, 6, 7, 8], // y-axis data for first trace
    width: [0.7, 0.7, 0.7, 0.7, 0.7, 0.7, 0.7, 0.7],
    type: 'bar',
    orientation: 'h',
    marker: {
      color: ['#FFBB64', '#FFBB64', '#FFBB64', '#FFBB64', '#FFBB64', '#FFBB64', '#FFBB64', '#FFFFFF'],

    }
  };


  @Input() text_elements: any = {
    header: 'Preferred metrics',
    subheader: 'Metrics used to measure consumer demand',
    caption: 'Caption',
    source: 'RetailX'
  }

  @Input() isEditMode: boolean = false;


  @ViewChild('chartContainer', {static: true}) chartContainer!: ElementRef;

  plotId = 'plotId'
  plotlyChart: any = null
  originalChart: any = null
  showChart: boolean = false;


  chartData: any = {
    data: [],
    layout: {
      legend: {
        orientation: 'h',
        y: 1.3,
        x: 0,
        traceorder: 'normal',
        font: {
          size: 10
        }
      },
      barcornerradius: 3,
      barmode: 'stack',
      font: {
        size: 10
      },
      xaxis: {
        visible: false,
        hoverformat: '.2f',
        //title: 'X-axis', // x-axis label
      },
      yaxis: {
        type: 'category',
        hoverformat: '.2f',
        //title: 'Y-axis' // y-axis label
      },

      uniformtext: {
        mode: 'hide',
        minsize: 10
      },

      margin: {r: 30, l: 150, t: 10, b: 20, pad: 4},
      height: 300,
      autosize: true

    },
    config: {
      displayModeBar: false,
      //staticPlot: this.common.isMobile()
    },
  };


  @HostListener('window:resize')
  onResize() {
    if (this.plotlyChart && this.plotContainer?.nativeElement) {
      const container = this.plotContainer.nativeElement.getBoundingClientRect();
      Plotly.relayout(this.plotlyChart, {width: container.width});
    }
  }

  ngOnInit() {
    this.chartData.data = this.data
    // console.log(this.data)
    this.plotId = `#plot${this.data[0].sqa_chart_id}`

    this.chartData.data.forEach((data: any) => {
      data.y = data.y.map((i: any) => {
        let charCount = i.length;
        let modifiedString = i;
        for (let j = 40; j < charCount; j += 40) {
          let spaceIndex = modifiedString.lastIndexOf(' ', j);
          if (spaceIndex !== -1) {
            modifiedString = modifiedString.slice(0, spaceIndex) + '<br>' + modifiedString.slice(spaceIndex);
          }
        }
        return modifiedString;
      });
    });
    this.chartData.layout.margin.l = this.barChartDynamicLeftMargin(this.chartData)


    this.consolidateChartData(this.chartData);
  }


  consolidateChartData(chartData: any): void {
    const chart = chartData.data[0];
    const {x, y, text, width} = chart;

    const isLikert = y.some((label: string) =>
      /strongly|disagree|agree|neutral/i.test(label)
    );

    type Bar = {
      x: number;
      y: string;
      text: string;
      width: number;
      index: number;
    };

    const bars: Bar[] = x.map((value: number, i: number) => ({
      x: value,
      y: y[i],
      text: text[i],
      width: width[i],
      index: i,
    }));

    const barsToKeep: Bar[] = [];
    const barsToGroup: Bar[] = [];

    bars
      .sort((a, b) => b.x - a.x)
      .forEach((bar, i) => {
        const label = bar.y.toLowerCase();
        const isOther = label === 'other';
        const shouldGroup = bar.x < 1 || i >= 9 || isOther;

        if (!isLikert && shouldGroup) {
          barsToGroup.push(bar);
        } else {
          barsToKeep.push(bar);
        }
      });

    let otherBar: Bar | null = null;

    if (barsToGroup.length > 0 && this.chartData?.data[0].questions_type !== 'multiple_selection') {
      const otherX = barsToGroup.reduce((sum, b) => sum + b.x, 0);

      otherBar = {
        x: otherX,
        y: 'Other',
        text: `${Math.round(otherX)}%`,
        width: 0.7,
        index: -1,
      };
    }

    barsToKeep.sort((a, b) => a.index - b.index);

    const finalBars = otherBar ? [otherBar, ...barsToKeep] : barsToKeep;

    chart.x = finalBars.map(b => b.x);
    chart.y = finalBars.map(b => b.y);
    chart.text = finalBars.map(b => b.text);
    chart.width = finalBars.map(b => b.width);
  }


  ngAfterViewInit() {
    // console.log(this.plotContainer.nativeElement.id);
    if (this.plotContainer?.nativeElement) {
      Plotly.newPlot(this.plotContainer.nativeElement, this.chartData.data, this.chartData.layout, this.chartData.config)
        .then((chart) => {
          this.plotlyChart = chart;
          this.originalChart = this.common.removeCircularReferences(this.plotlyChart);
          this.chartContainer.nativeElement = chart
        })
        .catch((error) => {
          console.error('Error rendering chart:', error);
        });
    } else {
      console.error('Chart container is not available in ngAfterViewInit.');
    }
  }

  barChartDynamicLeftMargin(chartData: any) {
    const chartDataY = chartData.data[0]?.y || [];

    // Calculate the longest string length from the y-axis labels
    const longestLabelLength = Math.max(...chartDataY.map((label: string) => label.length));

    // Adjust left margin based on the label length

    if (longestLabelLength < 40) {
      return longestLabelLength * 6.3; // Dynamic margin for labels less than 40 chars
    } else {
      this.chartData.layout.legend.x = -1; // Shift legend if necessary
      return 40 * 6; // Max margin for labels over 40 chars
    }
  }

}
